<template>
  <div class="container">
    <PageUnderMaintenance v-if="featureFlags?.main?.under_maintenance" />
    <template v-else>
      <NavigationBar v-if="showNav" />
      <div class="d-flex justify-center align-items-center">
        <RouterView class="views" :key="$route.fullPath" />
        <AiOptInModal v-if="showAiOptInModal" v-model:model-value="showAiOptInModal" />
        <UpdateContactAddressModal
          v-if="showUpdateAddressModal"
          v-model:model-value="showUpdateAddressModal"
        />
        <RegisteredEventModal
          v-if="showRegisteredEventModal && eventForModal"
          v-model:model-value="showRegisteredEventModal"
          :eventName="eventForModal?.eventName"
          :eventDates="eventForModal?.eventDates"
          :eventTime="eventForModal?.eventTime"
          :eventNameId="eventForModal?.eventNameId"
          :bannerImageUrl="eventForModal?.bannerImageUrl"
        />
      </div>
      <Snackbar ref="snackbar" style="z-index: 99999" />
    </template>
  </div>
</template>

<script>
import NavigationBar from '@/components/NavigationBar.vue';
import { useAuth0 } from '@auth0/auth0-vue';
import COMPANY_TYPE from '@common/constants/companyTypes';
import { getAiOptIn } from '@common/services/aip';
import { getContactAddressUpdated } from '@common/services/contact';
import Snackbar from '@design_system_v2/atoms/Snackbar.vue';
import PageUnderMaintenance from '@design_system_v2/components/PageUnderMaintenance.vue';
import flagsmith from 'flagsmith';
import { mapActions, mapGetters } from 'vuex';
// eslint-disable-next-line import/extensions
import { identifyUser, startTrackerService, trackerService } from '@/services/tracker';
// removing websocket for now
// import { initOnlinePresenceSocket } from '@/services/onlinePresence';
import AiOptInModal from './components/AiOptInModal.vue';
import RegisteredEventModal from './components/RegisteredEventModal.vue';
import UpdateContactAddressModal from './components/UpdateContactAddressModal.vue';

// Intercom stuff
const intercomAppId = process.env.INTERCOM_APP_ID;
const flagsmithApiKey = process.env.FLAGSMITH_API_KEY;

export default {
  name: 'MainApp',
  components: {
    PageUnderMaintenance,
    NavigationBar,
    Snackbar,
    AiOptInModal,
    UpdateContactAddressModal,
    RegisteredEventModal,
  },
  setup() {
    const auth0 = useAuth0();

    return { auth0 };
  },
  watch: {
    '$route.path': function (to) {
      this.checkAuthRequired(to);
    },
    'routePath.to.href': async function (to) {
      if (this.featureFlags?.all?.redirect_new_lists) {
        const oldFundsAndAllocatorsServiceProvidersList = [
          /funds\/list/,
          /allocators\/list/,
          /service_providers\/list/,
        ];
        const redirectToNewLists = oldFundsAndAllocatorsServiceProvidersList.some((r) =>
          r.test(to),
        );
        if (redirectToNewLists) {
          const searchParams = new URLSearchParams(window.location.search);
          const map = searchParams.get('map_view');
          if (!map || map === 'false') {
            const newUrls = {
              FundsMFe: '/list/funds',
              AllocatorsMFe: '/list/allocators',
              ServiceProvidersMFe: '/list/service-providers',
            };
            this.$router.push({ path: newUrls[this.$route.name], query: this.$route.query });
          }
        }
      }
      this.checkNavAvailability(to);
      this.checkAuthRequired(to);
      this.getEventsRegistered();
    },
    'featureFlags': {
      async handler() {
        // check if they need to opt in/out of ai
        if (
          this.featureFlags?.all?.enable_ai_tool &&
          this.companyTypeId === COMPANY_TYPE.Manager &&
          this.authRequired &&
          !this.registeredOnValorEvent
        ) {
          const lastDateShown = localStorage.getItem('date-ai-modal-shown')
            ? new Date(localStorage.getItem('date-ai-modal-shown'))
            : null;

          const ONE_DAY = 24 * 60 * 60 * 1000;

          // if pop up hasn't been shown in last 24 hrs
          if (!lastDateShown || new Date().getTime() - ONE_DAY > lastDateShown.getTime()) {
            await getAiOptIn()
              .then((res) => {
                if (res.aiOptInNeeded) {
                  this.showAiOptInModal = true;
                }
              })
              .catch(() => {});
          }
        }
      },
      immediate: true,
      deep: true,
    },
    async 'token'() {
      // removing websocket for now
      // if (this.token) {
      //  const onlinePresenceSocket = initOnlinePresenceSocket(this.token);
      //  onlinePresenceSocket.connect();
      // }
      if (this.token) {
        // check if they need to update address if not already on that page
        if (!window.location.pathname.includes('event/updateaddress')) {
          await getContactAddressUpdated()
            .then((res) => {
              if (res.addressUpdateNeeded) {
                this.showUpdateAddressModal = true;
              }
            })
            .catch(() => {});
        }
      }
      this.identifyUser();
    },
  },
  computed: {
    ...mapGetters('main', [
      'token',
      'isAdmin',
      'featureFlags',
      'currentExpectedPaths',
      'subscriber',
      'MFeModules',
      'companyTypeId',
    ]),
    registeredOnValorEvent() {
      const valorEvents = ['ValorAGM-2025', 'VirtualAGM-2025'];
      const registeredEvents = this.eventsRegisteredNameId ?? [];
      return valorEvents.some((event) => registeredEvents.includes(event));
    },
  },
  data() {
    return {
      showNav: true,
      eventsRegisteredId: null,
      eventsRegisteredNameId: null,
      routePath: {},
      authRequired: true,
      showAiOptInModal: false,
      showUpdateAddressModal: false,
      showRegisteredEventModal: true,
      eventForModal: null,
    };
  },
  async created() {
    // Create a global module manager
    window.manageModules = (MFeName, MFeApp) => {
      const allModules = this.MFeModules;

      if (!allModules[MFeName] && MFeApp) {
        this.addModule({ MFeName, MFeApp });
      }
    };

    await this.getFeatureFlags();
    this.checkNavAvailability(window.location.pathname);

    // Add an event listener to the iConnections Events Bus
    window.addEventListener('message', async (event) => {
      if (event && event.data && event.data.name === 'iConnectionsEventBus') {
        // Get feature flags that might be usefully here
        const mainFeatureFlags = this.featureFlags.main || {};
        const allFeatureFlags = this.featureFlags.all || {};

        // Depending on the event data type...
        switch (event.data.type) {
          case 'changeMFe': {
            // Update the user info on trackers to make sure they are up to date
            identifyUser();

            // Update intercom
            window.Intercom('update');

            // Redirects...
            window.location.href = event.data.path;
            break;
          }

          case 'handleNavigationHistory':
            if (event.data?.to?.href && allFeatureFlags?.new_inbox_redirect) {
              const redirectMeetingListToInbox = event.data?.to?.href?.includes('/meetings/list');
              if (redirectMeetingListToInbox && event.data?.to?.name !== 'login') {
                this.$router.push('/inbox');
                return;
              }
            }
            this.routePath = {
              baseUrl: event.data.baseUrl,
              to: event.data.to,
            };
            this.handleNavigationHistory(event.data.baseUrl, event.data.from, event.data.to);
            break;

          case 'showSnackbar':
            this.$refs.snackbar.show({
              message: event.data.message || '',
              type: event.data.snackbarType || 'success',
              timeout: event.data.timeout || 2000,
            });
            break;

          case 'login':
            // Handles login events
            await this.setToken({
              token: event.data.token,
              refreshToken: event.data.refreshToken,
            });
            window.postMessage(
              {
                name: 'iConnectionsEventBus',
                type: 'changeMFe',
                path: event.data.redirect || '/',
              },
              '*',
            );
            break;

          case 'refreshTokens':
            await this.setToken({
              token: event.data.token,
              refreshToken: event.data.refreshToken,
            });
            break;

          case 'logout':
            // Handle logout events
            window.Intercom('shutdown');
            this.deleteToken();
            if (allFeatureFlags.login_password || mainFeatureFlags.auth_logout) {
              await this.auth0.logout();
            }
            this.checkNavAvailability('/login');
            this.$router.push('/login');
            break;

          case 'termsRequired':
            this.termsRequired(true);
            window.postMessage(
              { name: 'iConnectionsEventBus', type: 'changeMFe', path: '/terms_of_service' },
              '*',
            );
            break;

          case 'termsAccepted':
            window.postMessage(
              { name: 'iConnectionsEventBus', type: 'changeMFe', path: event.data.path || '/' },
              '*',
            );
            break;

          case 'aiqpRequired':
            this.aiqpRequired(true);
            window.postMessage(
              { name: 'iConnectionsEventBus', type: 'changeMFe', path: '/aiqp' },
              '*',
            );
            break;

          case 'aiqpDone':
            this.aiqpRequired(false);
            window.postMessage(
              { name: 'iConnectionsEventBus', type: 'changeMFe', path: event.data.path || '/' },
              '*',
            );
            break;

          case 'onboardingRequired':
            this.onboardingRequired(true);
            // Re-route to / so the router can handle the onboarding
            window.postMessage(
              { name: 'iConnectionsEventBus', type: 'changeMFe', path: '/onboarding' },
              '*',
            );
            break;

          case 'onboardingDone':
            this.onboardingRequired(false);
            // Redirect to the event path or to / if nothing was provided
            window.postMessage(
              { name: 'iConnectionsEventBus', type: 'changeMFe', path: event.data.path || '/' },
              '*',
            );
            break;

          case 'changeUser':
            this.changeUser({ ...event.data.payload });
            break;

          case 'tracker':
            trackerService(event.data, this.$route, process.env.DEBUG, this.eventsRegisteredId);
            break;

          case 'refreshNotificationsCount':
            await this.getNotificationsCount();
            break;

          default:
            break;
        }
      }
    });
  },
  async mounted() {
    // And also check for them every 5 minutess
    setInterval(this.getFeatureFlags, 5 * 60000);
    startTrackerService();
  },
  methods: {
    ...mapActions('main', [
      'retrieveEventsRegistered',
      'getFeatureFlags',
      'setToken',
      'deleteToken',
      'termsRequired',
      'aiqpRequired',
      'onboardingRequired',
      'onboardingRequired',
      'changeUser',
      'addModule',
      'getNotificationsCount',
    ]),
    checkAuthRequired(path) {
      const publicPages = [
        /success/,
        /login/,
        /redirect-auth/,
        /event\/registration/,
        /event\/referral/,
        /event\/updateaddress/,
        /event\/meetupinvite/,
        /event\/updatepreferences/,
        /event\/updateheadshot/,
        /event\/addcoo/,
        /roadshows\/.*\/contact/,
        /roadshows\/[^/]+\/invite\/[^/]+/,
        /roadshows\/[^/]+\/booking/,
        /roadshows\/[^/]+\/create-basic-account\/[^/]+/,
        /user\/[^/]+\/confirmation\/[^/]+/,
        /event\/details\/[^/]+\/registration/,
        /event\/details\/[^/]+\/badgeConfirmation/,
        /event\/details\/[^/]+\/contactstable/,
      ]; // All main paths that dont require auth
      // including empty / path here to handle login redirects

      this.authRequired = !publicPages.some((r) => r.test(path)) && path !== '/';
    },
    checkNavAvailability(path) {
      if (path.includes('logout')) {
        window.postMessage({ name: 'iConnectionsEventBus', type: 'logout' }, '*');
      }
      const noNavRoute = [
        /success/,
        /login/,
        /redirect-auth/,
        /aiqp/,
        /terms_of_service/,
        /calendar_sync/,
        /onboarding/,
        /event\/registration/,
        /event\/details\/[^/]+\/registration/,
        /event\/details\/[^/]+\/badgeConfirmation/,
        /event\/details\/[^/]+\/contactstable/,
        /event\/referral/,
        /event\/updateaddress/,
        /event\/updatepreferences/,
        /event\/updateheadshot/,
        /event\/addcoo/,
        /event\/meetupinvite/,
        /event\/details\/.*\/print\/meetings/,
        /event\/details\/.*\/print\/agenda/,
        /documents\/fund\/[^/]+/,
        /roadshows\/unsubscribe/,
        /roadshows\/.*\/contact/,
        /roadshows\/[^/]+\/invite\/[^/]+/,
        /meetings\/redirect/,
        /user\/[^/]+\/confirmation\/[^/]+/,
      ]; // All paths that do not have a nav bar
      this.showNav = !noNavRoute.some((r) => r.test(path));
    },
    handleNavigationHistory(baseUrlParam, from, to) {
      // To prevent an issue where the unmounted MFe triggers a afterEach event, we compare
      // the baseURL received with the currentPath. If they don't match, ignore the history update

      // 1 - re-parse the currentExpectedPaths array to remove all /
      const cleanedCurrentExpectedPaths = this.currentExpectedPaths.map((path) =>
        path.replace(/\//g, ''),
      );
      // 2 - Cleanup all the / from the baseUrl
      const baseUrl = baseUrlParam.replace(/\//g, '');
      // 3 - Check if the baseUrl (without the /) is within the currentExpectedPaths
      if (!cleanedCurrentExpectedPaths.includes(baseUrl)) return;

      // Update the history
      const historyState = { ...window.history.state };
      if (historyState && historyState.current) {
        let fromHref = from.href || null;
        let toHref = to.href || null;

        // If the TO path is the same as the current page - ignore the history update
        if (historyState.current === toHref) return;

        // Clean the hrefs
        if (fromHref) {
          // Remove double slashes
          fromHref = fromHref.replace(/\/\//g, '/');

          // Remove initial slash
          if (fromHref[0] === '/') {
            fromHref = fromHref.substring(1);
          }
        }

        if (toHref) {
          // Remove double slashes
          toHref = toHref.replace(/\/\//g, '/');

          // Remove initial slash
          if (toHref[0] === '/') {
            toHref = toHref.substring(1);
          }
        }

        // Lets compare if the from and to came from the same service
        const fromRoot = fromHref ? fromHref.split('/')[0] : null;
        const toRoot = toHref ? toHref.split('/')[0] : null;

        if (fromRoot && toRoot && fromRoot === toRoot) {
          // Handle INTERNAL navigation ONLY (inside this single MFE)
          window.historyBackPath = to.href;
          historyState.back = from.href;
          historyState.current = to.href;
        } else {
          // Handle EXTERNAL navigation ONLY (between different MFEs)
          historyState.back = window.historyBackPath;
          historyState.current = to.href;
        }
        window.history.replaceState(historyState, '');
      }
    },
    async getEventsRegistered() {
      const eventsRegistered = await this.retrieveEventsRegistered();
      this.eventsRegisteredId = eventsRegistered?.map((event) => event?.eventId);
      this.eventsRegisteredNameId = eventsRegistered?.map((event) => event?.eventNameId);

      const valorEventNameIds = ['ValorAGM-2025', 'VirtualAGM-2025'];
      const valorEvent = eventsRegistered.find((e) => valorEventNameIds.includes(e.eventNameId));

      const eventShown = localStorage.getItem('event-modal-shown');
      if (
        valorEvent &&
        this.$route.path?.startsWith('/dashboard') &&
        eventShown !== valorEvent.eventNameId
      ) {
        this.eventForModal = valorEvent;
      }
    },
    identifyUser() {
      if (this.token) {
        const splitJWT = this.token.split('.');
        const jwtData = splitJWT[1];
        const parsedJWT = JSON.parse(atob(jwtData));
        const {
          ContactID,
          ContactGUID,
          FirstName,
          LastName,
          Email,
          CompanyID,
          CompanyTypeID,
          IsCOO,
        } = parsedJWT;

        // Parse CompanyTypeID
        let companyType = '';
        switch (CompanyTypeID) {
          case 1:
            companyType = 'Fund';
            break;
          case 2:
            companyType = 'Investor';
            break;
          case 3:
            companyType = 'Service Provider';
            break;
          case 4:
            companyType = 'iConnections';
            break;
          default:
            companyType = 'Unknown';
        }

        // Create Intercom user
        if (intercomAppId && ContactID) {
          window.Intercom('boot', {
            api_base: 'https://api-iam.intercom.io',
            app_id: intercomAppId,
            name: `${FirstName} ${LastName}`,
            email: Email,
            ContactID,
            CompanyTypeID,
            companyType,
            IsCOO: IsCOO || 0,
          });
        }

        // Identify the user on Flagsmith
        flagsmith.init({
          api: process.env.FLAGSMITH_URL,
          enableAnalytics: true,
          environmentID: flagsmithApiKey || '',
          identity: ContactID,
          traits: {
            ContactID,
            ContactGUID,
            Email,
            Subscriber: this.subscriber,
            CompanyID,
            CompanyTypeID,
          },
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@foundation';

.container {
  min-height: 100vh;
  height: 100%;
  width: 100%;
  background-color: map-get($neutral, 'neutral-50');
}

.views {
  max-width: 100vw;
}
</style>
